import React, { useState, useEffect, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation, useNavigate } from "react-router-dom";

// Kritik (İlk açılışta görünen) bileşenler normal import edilir
import { Navigation } from "./components/navigation";
import { Header } from "./components/header";
import { About } from "./components/about";
import { Gallery } from "./components/gallery";
import { MouseEffect } from "./components/Mouse_Effect";

import JsonDataTR from "./data/data_tr.json";
import JsonDataEN from "./data/data_en.json";
import JsonDataRU from "./data/data_ru.json";

import "./App.css"; 
import { Helmet } from "react-helmet-async"; 

// 🚀 ÇÖZÜM: Kritik olmayan / Ağır bileşenleri "Lazy" ile bölüyoruz (Performans Tasarrufu)
const Contact = lazy(() => import("./components/contact").then(module => ({ default: module.Contact })));
const ProductDetail = lazy(() => import("./components/ProductDetail").then(module => ({ default: module.ProductDetail })));

const dataMap = {
  tr: JsonDataTR,
  en: JsonDataEN,
  ru: JsonDataRU
};

// =========================================================================
// YENİ EKLENDİ: TÜM BUTONLARDAKİ AÇIK MAVİ PARLAMALARI EZEN GLOBAL STİL
// =========================================================================
const GlobalThemeOverride = () => (
  <style>
    {`
      /* TÜM BUTONLARDAKİ AÇIK MAVİ PARLAMAYI (GLOW) İPTAL EDİP TURUNCU YAPIYORUZ */
      .btn-custom {
        background-color: #e5882a !important;
        border: 2px solid #e5882a !important;
        color: #171717 !important;
        box-shadow: 0 0 10px rgba(229, 136, 42, 0.4) !important; /* Turuncu Gölge */
        transition: all 0.3s ease;
      }
      .btn-custom:hover {
        background-color: transparent !important;
        color: #e5882a !important;
        border: 2px solid #e5882a !important;
        box-shadow: 0 0 20px rgba(229, 136, 42, 0.6) !important; /* Hoverda turuncu parlar */
      }
      
      /* Ürünler Menüsündeki Butonların Aktif Hali */
      .btn-filter.active {
         background-color: #171717 !important;
         color: #e5882a !important;
         border-color: #171717 !important;
      }
    `}
  </style>
);

// --- YUKARI ÇIK BUTONU BİLEŞENİ ---
const ScrollToTop = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.scrollY > 100) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };
    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  if (!isVisible) return null;

  return (
    <button
      onClick={scrollToTop}
      style={{
        position: "fixed",
        bottom: "40px",
        right: "40px",
        zIndex: 999999, 
        backgroundColor: "#171717", 
        color: "#e5882a",           
        border: "2px solid #e5882a", 
        borderRadius: "50%",
        width: "50px",
        height: "50px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        cursor: "pointer",
        transition: "all 0.3s ease",
        boxShadow: "0 4px 15px rgba(0,0,0,0.4)", 
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "#e5882a"; 
        e.currentTarget.style.color = "#171717";           
        e.currentTarget.style.border = "2px solid #171717"; 
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "#171717"; 
        e.currentTarget.style.color = "#e5882a";           
        e.currentTarget.style.border = "2px solid #e5882a"; 
      }}
      title="Yukarı Çık"
    >
      <svg 
        width="24" 
        height="24" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2.5" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      >
        <line x1="12" y1="19" x2="12" y2="5"></line>
        <polyline points="5 12 12 5 19 12"></polyline>
      </svg>
    </button>
  );
};

// --- ANA SAYFA BİLEŞENİ ---
const Home = ({ landingPageData, lang }) => {
  const seoData = landingPageData.seo;
  const currentUrl = `https://www.omemtech.com/${lang}`;

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "OmemTech",
    "url": "https://www.omemtech.com/",
    "logo": "https://www.omemtech.com/img/omemtech.png",
    "description": seoData.description,
    "sameAs": [
      "https://www.linkedin.com/company/omemtech", 
      "https://www.instagram.com/omemtech"         
    ]
  };

  return (
    <div>
      <Helmet>
        <html lang={lang} />
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        
        <link rel="canonical" href={currentUrl} />
        <link rel="alternate" hreflang="tr" href="https://www.omemtech.com/tr" />
        <link rel="alternate" hreflang="en" href="https://www.omemtech.com/en" />
        <link rel="alternate" hreflang="ru" href="https://www.omemtech.com/ru" />
        <link rel="alternate" hreflang="x-default" href="https://www.omemtech.com/tr" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content="https://www.omemtech.com/img/omemtech.png" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={currentUrl} />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content="https://www.omemtech.com/img/omemtech.png" />

        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>
      
      <Header data={landingPageData.Header} lang={lang} />
      <About data={landingPageData.About} lang={lang} />
      <Gallery data={landingPageData.Gallery} lang={lang} />
      
      <Suspense fallback={<div style={{ textAlign: "center", padding: "50px" }}>Yükleniyor...</div>}>
        <Contact data={landingPageData.Contact} lang={lang} />
      </Suspense>
    </div>
  );
};

// --- ROTA SARMALAYICILARI ---
const HomeWrapper = () => {
  const { lang } = useParams();
  if (!["tr", "en", "ru"].includes(lang)) return <Navigate to="/tr" replace />;
  return <Home landingPageData={dataMap[lang]} lang={lang} />;
};

const ProductDetailWrapper = () => {
  const { lang } = useParams();
  if (!["tr", "en", "ru"].includes(lang)) return <Navigate to="/tr" replace />;
  return <ProductDetail lang={lang} />;
};

// =========================================================================
// YENİ: ESKİ GOOGLE LİNKLERİNİ YAKALAYIP TÜRKÇE SAYFAYA FIRLATAN KURTARICI
// =========================================================================
const OldProductRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/tr/urun/${id}`} replace />;
};

// --- ANA YAPI ---
const MainLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["header", "about", "portfolio", "contact"];
      const scrollPosition = window.scrollY + 300;

      sections.forEach((id) => {
        const element = document.getElementById(id);
        if (
          element &&
          scrollPosition >= element.offsetTop &&
          scrollPosition < element.offsetTop + element.offsetHeight
        ) {
          setActiveSection(id);
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []); 

  const validLangs = ["tr", "en", "ru"];
  const pathParts = location.pathname.split("/");
  const currentLang = validLangs.includes(pathParts[1]) ? pathParts[1] : "tr";

  const changeLanguageUrl = (newLang) => {
    if (location.pathname === "/" || location.pathname === "") {
      navigate(`/${newLang}`);
    } else {
      const newPath = location.pathname.replace(`/${currentLang}`, `/${newLang}`);
      navigate(newPath);
    }
  };

  return (
    <>
      <Navigation lang={currentLang} setLang={changeLanguageUrl} activeSection={activeSection} /> 
      
      <Suspense fallback={<div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#e5882a", backgroundColor: "#171717" }}>Yükleniyor...</div>}>
        <Routes>
          <Route path="/" element={<Navigate to="/tr" replace />} />
          <Route path="/:lang" element={<HomeWrapper />} />
          <Route path="/:lang/urun/:id" element={<ProductDetailWrapper />} />
          
          {/* YENİ: Eski Google aramalarından gelenleri Türkçe detaya yönlendirir */}
          <Route path="/urun/:id" element={<OldProductRedirect />} />
          
          {/* YENİ: Sitede var olmayan saçma sapan bir link girilirse ana sayfaya atar (Boş ekranı engeller) */}
          <Route path="*" element={<Navigate to="/tr" replace />} />
        </Routes>
      </Suspense>
      
      <ScrollToTop />
    </>
  );
};

// --- UYGULAMA BAŞLANGICI ---
const App = () => {
  return (
    <Router>
      {/* GLOBAL TEMAYI EZEN COMPONENT BURAYA EKLENDİ */}
      <GlobalThemeOverride />
      <MouseEffect />
      <MainLayout />
    </Router>
  );
};

export default App;